/* eslint-disable @typescript-eslint/naming-convention */
import { ITimeoutService } from "angular";
import { Ng1StateDeclaration, StateParams, StateService } from "@uirouter/angularjs";
import { IModalServiceInstance } from "angular-ui-bootstrap";
import { featureAvailable, featureToggleEnabled } from "./resolveGuards";
import { IGtmhubRootScopeService } from "@gtmhub/models";
import { modal } from "./core/routing/util";
import { IEditionPlanChangeResolveParams } from "./edition-plan-change";
import { FeatureFlag } from "./featureToggle/models";

export class StateFactory {
  private _bootstrapStates: AccountBootstrapStates;
  private _bootstrapModalStates: AccountBootstrapModalStates;
  private _accountStates: AccountStates;
  private _workdayState: WorkdayState;
  private _configurationStates: ConfigurationStates;
  private _goalStates: GoalStates;
  private _homeStates: HomeStates;
  private _globalSearchStates: GlobalSearchStates;
  private _multiAccountStates: MultiAccountStates;
  private _planningSessionStates: PlanningSessionStates;
  private _roleStates: RoleStates;
  private _miscStates: MiscStates;
  private _approveBadgesStates: ApproveBadgesStates;
  private _badgeStates: BadgeStates;
  private _employeeStates: EmployeeStates;
  private _teamStates: TeamStates;
  private _reportingStates: ReportingStates;
  private _automationStates: AutomationStates;
  private _inboxStates: InboxStates;
  private _notificationSettingsStates: NotificationSettingsStates;
  private _workflowStates: WorkflowStates;
  private _assigneeStates: AssigneeStates;
  private _taskStates: TaskStates;
  private _kpiStates: KpiStates;
  private _listStates: ListStates;
  private _notificationsUrlsRedirectStates: NotificationsUrlsRedirectStates;
  private _legacyRedirectStates: LegacyRedirectStates;
  private _googleSitesStates: GoogleSitesStates;
  private _manageCustomFieldsOrderStates: ManageCustomFieldsOrderStates;

  public default(): Ng1StateDeclaration {
    return {
      url: "/",
      data: {
        trackingName: "Home Page",
      },
      redirectTo: { state: "gtmhub.home" },
    };
  }

  public rootProtectedState(): Ng1StateDeclaration {
    return {
      abstract: true,
      templateUrl: require("./main.jade"),
      data: {
        requiresLogin: true,
        requiresJwtProfile: true,
        requiresAccount: true,
      },
    };
  }

  public errorState(): Ng1StateDeclaration {
    return {
      url: "/error/",
      params: {
        error: null,
      },
      data: {
        requiresLogin: false,
        trackingPage: "Error Page",
      },
      templateUrl: require("./shared/views/error.jade"),
      controller: "ErrorCtrl",
    };
  }

  public accountBootstrapStates(): AccountBootstrapStates {
    if (!this._bootstrapStates) {
      this._bootstrapStates = new AccountBootstrapStates();
    }
    return this._bootstrapStates;
  }

  public accountBootstrapModalStates(): AccountBootstrapModalStates {
    if (!this._bootstrapModalStates) {
      this._bootstrapModalStates = new AccountBootstrapModalStates();
    }
    return this._bootstrapModalStates;
  }

  public assigneeStates(): AssigneeStates {
    if (!this._assigneeStates) {
      this._assigneeStates = new AssigneeStates();
    }
    return this._assigneeStates;
  }

  public taskStates(): TaskStates {
    if (!this._taskStates) {
      this._taskStates = new TaskStates();
    }
    return this._taskStates;
  }

  public accountStates(): AccountStates {
    if (!this._accountStates) {
      this._accountStates = new AccountStates();
    }
    return this._accountStates;
  }

  public workdayState(): WorkdayState {
    if (!this._workdayState) {
      this._workdayState = new WorkdayState();
    }
    return this._workdayState;
  }

  public configurationStates(): ConfigurationStates {
    if (!this._configurationStates) {
      this._configurationStates = new ConfigurationStates();
    }
    return this._configurationStates;
  }

  public manageCustomFieldsOrderStates(): ManageCustomFieldsOrderStates {
    if (!this._manageCustomFieldsOrderStates) {
      this._manageCustomFieldsOrderStates = new ManageCustomFieldsOrderStates();
    }
    return this._manageCustomFieldsOrderStates;
  }

  public goalStates(): GoalStates {
    if (!this._goalStates) {
      this._goalStates = new GoalStates();
    }
    return this._goalStates;
  }

  public homeStates(): HomeStates {
    if (!this._homeStates) {
      this._homeStates = new HomeStates();
    }
    return this._homeStates;
  }

  public globalSearchStates(): GlobalSearchStates {
    if (!this._globalSearchStates) {
      this._globalSearchStates = new GlobalSearchStates();
    }
    return this._globalSearchStates;
  }

  public multiAccountStates(): MultiAccountStates {
    if (!this._multiAccountStates) {
      this._multiAccountStates = new MultiAccountStates();
    }
    return this._multiAccountStates;
  }

  public planningSessionStates(): PlanningSessionStates {
    if (!this._planningSessionStates) {
      this._planningSessionStates = new PlanningSessionStates();
    }
    return this._planningSessionStates;
  }

  public roleStates(): RoleStates {
    if (!this._roleStates) {
      this._roleStates = new RoleStates();
    }
    return this._roleStates;
  }

  public miscStates(): MiscStates {
    if (!this._miscStates) {
      this._miscStates = new MiscStates();
    }
    return this._miscStates;
  }

  public approveBadgesStates(): ApproveBadgesStates {
    if (!this._approveBadgesStates) {
      this._approveBadgesStates = new ApproveBadgesStates();
    }
    return this._approveBadgesStates;
  }

  public badgeStates(): BadgeStates {
    if (!this._badgeStates) {
      this._badgeStates = new BadgeStates();
    }
    return this._badgeStates;
  }

  public employeeStates(): EmployeeStates {
    if (!this._employeeStates) {
      this._employeeStates = new EmployeeStates();
    }
    return this._employeeStates;
  }

  public teamStates(): TeamStates {
    if (!this._teamStates) {
      this._teamStates = new TeamStates();
    }
    return this._teamStates;
  }

  public reportingStates(): ReportingStates {
    if (!this._reportingStates) {
      this._reportingStates = new ReportingStates();
    }
    return this._reportingStates;
  }

  public automationStates(): AutomationStates {
    if (!this._automationStates) {
      this._automationStates = new AutomationStates();
    }
    return this._automationStates;
  }

  public inboxStates(): InboxStates {
    if (!this._inboxStates) {
      this._inboxStates = new InboxStates();
    }
    return this._inboxStates;
  }

  public notificationSettingsStates(): NotificationSettingsStates {
    if (!this._notificationSettingsStates) {
      this._notificationSettingsStates = new NotificationSettingsStates();
    }
    return this._notificationSettingsStates;
  }

  public workflowStates(): WorkflowStates {
    if (!this._workflowStates) {
      this._workflowStates = new WorkflowStates();
    }
    return this._workflowStates;
  }

  public kpiStates(): KpiStates {
    if (!this._kpiStates) {
      this._kpiStates = new KpiStates();
    }
    return this._kpiStates;
  }

  public listStates(): ListStates {
    if (!this._listStates) {
      this._listStates = new ListStates();
    }
    return this._listStates;
  }

  public notificationsUrlsRedirectStates(): NotificationsUrlsRedirectStates {
    if (!this._notificationsUrlsRedirectStates) {
      this._notificationsUrlsRedirectStates = new NotificationsUrlsRedirectStates();
    }
    return this._notificationsUrlsRedirectStates;
  }

  public legacyRedirectStates(): LegacyRedirectStates {
    if (!this._legacyRedirectStates) {
      this._legacyRedirectStates = new LegacyRedirectStates();
    }
    return this._legacyRedirectStates;
  }

  public googleSitesStates(): GoogleSitesStates {
    if (!this._googleSitesStates) {
      this._googleSitesStates = new GoogleSitesStates();
    }
    return this._googleSitesStates;
  }
}

class AccountBootstrapModalStates {
  public accountBootstrapModalRoot(): Ng1StateDeclaration {
    return modal({
      view: "accountBootstrapModalView",
      windowClass: "modal",
      abstract: true,
      backdrop: false,
    });
  }

  public accountLinkedRoot(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: false,
        requiresJwtProfile: false,
        requiresAccount: false,
        trackingPage: "Account Linked",
      },
      params: {
        accountId: null,
        billingUrl: null,
      },
      views: {
        "accountBootstrapModalView@": {
          controller: "AccountLinkedCtrl",
          templateUrl: require("./login/views/logout-linked.jade"),
        },
      },
      url: "/account-linked",
    };
  }

  public accountSwitchedRoot(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: false,
        requiresJwtProfile: false,
        requiresAccount: false,
        trackingPage: "Account Switched",
      },
      params: {
        accountId: null,
        billingUrl: null,
      },
      views: {
        "accountBootstrapModalView@": {
          controller: "AccountSwitchedCtrl",
          templateUrl: require("./login/views/logout-switched.jade"),
        },
      },
      url: "/account-switched",
    };
  }
}

class AccountBootstrapStates {
  public accountBootstrapRoot(): Ng1StateDeclaration {
    return {
      abstract: true,
      data: {
        requiresLogin: true,
        requiresJwtProfile: true,
        requiresAccount: false,
      },
      template: "<ui-view/>",
    };
  }

  public accountSuspendedRoot(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: false,
        requiresJwtProfile: false,
        requiresAccount: true,
        trackingPage: "Account Suspended",
        styleUrl: require("./edition-plan-change/less/edition-plan-change.less"),
      },
      params: {},
      url: "/account-suspended",
      controller: "AccountSuspendedCtrl",
      templateUrl: require("./login/views/login-suspended.jade"),
    };
  }

  public userDeactivatedRoot(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: false,
        requiresJwtProfile: false,
        requiresAccount: false,
        styleUrl: require("./login/less/user-deactivated.less"),
      },
      url: "/user-deactivated",
      params: {
        transactionID: {
          value: null,
        },
      },
      controller: "UserDeactivatedCtrl",
      templateUrl: require("./login/views/user-deactivated.jade"),
    };
  }

  public resolveToken(): Ng1StateDeclaration {
    return {
      data: {
        disableAnalytics: true,
        requiresLogin: false,
        requiresJwtProfile: false,
        requiresAccount: false,
      },
      url: "/resolve-token/",
      templateUrl: require("./shared/views/app-skeleton.jade"),
      controller: "ResolveTokenCtrl",
    };
  }

  public resolveAccount(): Ng1StateDeclaration {
    return {
      data: {
        disableAnalytics: true,
      },
      url: "/resolve-account/",
      templateUrl: require("./shared/views/app-skeleton.jade"),
      controller: "ResolveAccountCtrl",
    };
  }

  public createAccount(): Ng1StateDeclaration {
    return {
      templateUrl: require("./login/views/create-account.jade"),
      controller: "CreateAccountController",
    };
  }
}

class KpiStates {
  public kpis(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        titleKey: "kpis",
        trackingPage: "KPIs",
        styleUrl: require("./kpis/less/kpis.less"),
      },
      url: "/kpis/",
      templateUrl: require("./kpis/views/kpis.jade"),
      controller: "KpisCtrl",
      resolve: {
        _featureAvailable: featureAvailable("kpis") && featureToggleEnabled(FeatureFlag.ModuleKpis),
      },
    };
  }

  public _singleKpi(): Ng1StateDeclaration {
    return modal({
      view: "kpiform",
      windowClass: "medium-large kpi-modal",
      backdropClass: "dark-backdrop",
      abstract: true,
    });
  }

  public form(returnState = "^.^"): Ng1StateDeclaration {
    return {
      url: ":kpiId/:selectedKpiCellGroupId",
      data: {
        requiresLogin: true,
        trackingPage: "KPI Details",
      },
      params: {
        returnState,
      },
      views: {
        "kpiform@": {
          templateUrl: require("./kpis/views/form.jade"),
          controller: "KpiFormCtrl",
        },
      },
      resolve: {
        formData: [
          "$stateParams",
          ($stateParams: StateParams) => {
            return {
              kpiId: $stateParams.kpiId,
              selectedKpiCellGroupId: $stateParams.selectedKpiCellGroupId,
            };
          },
        ],
      },
    };
  }

  public createNew(): Ng1StateDeclaration {
    return {
      url: "new/",
      data: {
        requiresLogin: true,
        trackingPage: "New KPI",
      },
      views: {
        "kpiform@": {
          templateUrl: require("./kpis/views/form.jade"),
          controller: "KpiFormCtrl",
        },
      },
      resolve: {
        formData: () => ({}),
      },
    };
  }
}

class TaskStates {
  public _tasks(): Ng1StateDeclaration {
    return modal({
      view: "taskview",
      windowClass: "modal",
      abstract: true,
    });
  }

  public archived(): Ng1StateDeclaration {
    return {
      url: "archived/",
      data: {
        modal: null,
        requiresLogin: true,
        trackingPage: "Archived",
      },
      views: {
        "taskview@": {
          templateUrl: require("./tasks/views/archived.jade"),
          controller: "ArchivedTasksCtrl",
        },
      },
    };
  }

  public _task(): Ng1StateDeclaration {
    return modal({
      data: {
        styleUrl: require("./tasks/components/task-form.less"),
        trackingPage: "Task View",
      },
      view: "taskview",
      windowClass: "sidebar-modal task-form-modal",
      abstract: true,
    });
  }

  public task(): Ng1StateDeclaration {
    return {
      url: "task/:taskId/?parentId",
      data: {
        modal: null,
        requiresLogin: true,
        titleExpression: "task.name",
        styleUrl: require("./tasks/components/task-form.less"),
        trackingPage: "Task Details",
      },
      params: {
        parentType: null,
      },
      views: {
        "taskview@": {
          component: "taskForm",
        },
      },
    };
  }

  public tasks(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        styleUrl: require("./tasks/less/tasks.less"),
        trackingPage: "Tasks",
      },
      url: "/tasks/?filterId",
      templateUrl: require("./tasks/views/tasks.jade"),
      controller: "TasksCtrl",
      resolve: {
        _featureAvailable: featureToggleEnabled(FeatureFlag.ModuleTasks),
      },
    };
  }
}

class AssigneeStates {
  public _filter(): Ng1StateDeclaration {
    return modal({
      view: "filterform",
      windowClass: "medium-small filter-modal",
      abstract: true,
      data: {
        trackingPage: "Assignees Filter Form",
      },
      url: "filter/",
    });
  }

  public filter(): Ng1StateDeclaration {
    return {
      url: ":formFilterId/",
      data: {
        modal: null,
        requiresLogin: true,
        trackingPage: "Assignees Current Filter Form",
      },
      views: {
        "filterform@": {
          templateUrl: require("./assignees/views/filter-form.jade"),
          controller: "FilterFormCtrl",
        },
      },
    };
  }

  public _ownerSelector(): Ng1StateDeclaration {
    return modal({
      view: "ownersview",
      windowClass: "sidebar-modal",
      abstract: true,
      url: "select-owner/",
    });
  }

  public ownerSelector(): Ng1StateDeclaration {
    return {
      url: "select/?selectionType&principalIds",
      data: {
        modal: null,
        requiresLogin: true,
        trackingPage: "Current Select Owner",
      },
      views: {
        "ownersview@": {
          templateUrl: require("./assignees/views/owner-selector.jade"),
          controller: "OwnerSelectorCtrl",
        },
      },
    };
  }
}

class AccountStates {
  private modal: IModalServiceInstance;

  public usersList(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        titleKey: "users_cap",
        trackingPage: "Users",
      },
      url: "/users/",
      templateUrl: require("./users/views/users-list.jade"),
      controller: "UsersListCtrl",
    };
  }

  public _userDetailView(): Ng1StateDeclaration {
    return modal({
      view: "userdetailview",
      windowClass: "user-profile-sidebar",
      abstract: true,
      data: {
        trackingPage: "User Detail View",
      },
    });
  }

  public userDetailView(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        titleExpression: "user.firstName + ' ' + user.lastName",
        trackingPage: "User Detail View",
      },
      url: ":id/user-details/",
      views: {
        "userdetailview@": {
          templateUrl: require("./users/views/user-detail-view.jade"),
          controller: "UserDetailsViewCtrl",
        },
      },
    };
  }

  public userProfile(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        titleKey: "user_profile",
        trackingPage: "User Profile",
        styleUrl: require("@gtmhub/users/userProfile/less/user-profile.less"),
      },
      url: "/user-profile/",
      templateUrl: require("@gtmhub/users/userProfile/views/user-profile.jade"),
      controller: "UserProfileCtrl",
    };
  }

  public _avatar(): Ng1StateDeclaration {
    return modal({
      view: "modalview",
      windowClass: "modal-small vertical-center",
      backdropClass: "dark-backdrop",
      abstract: true,
      // Disabling the backdrop, because the mousedown event has stopPropagation inside the ng-img-crop library itself
      // and we cannot fix the behavior of “mousedown in the form followed by mouseup outside the form” because of that.
      // I.e. setting backdrop=false, in order to prevent accidental form close when the user is resizing his avatar.
      backdrop: false,
    });
  }

  public uploadAvatar(): Ng1StateDeclaration {
    return {
      url: "avatar/",
      views: {
        "modalview@": {
          templateUrl: require("@gtmhub/users/userProfile/views/avatar.jade"),
          controller: "AvatarCtrl",
        },
      },
    };
  }

  public changeUserAvatar(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        trackingPage: "Change User Avatar",
      },
      url: "change-avatar/",
      views: {
        "modalview@": {
          templateUrl: require("@gtmhub/users/userProfile/views/avatar.jade"),
          controller: "ChangeUserAvatarCtrl",
        },
      },
    };
  }

  public _edit(): Ng1StateDeclaration {
    return modal({
      view: "editModal",
      windowClass: "medium edit-user-modal",
      abstract: true,
      backdrop: false,
    });
  }

  public avatarDone(): Ng1StateDeclaration {
    return {
      onEnter: [
        "$state",
        ($state: StateService) => {
          this.modal.close();
          $state.go("^.^");
        },
      ],
    };
  }

  public sendChangePasswordRequest(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        titleKey: "user_profile",
        styleUrl: require("@gtmhub/users/userProfile/less/user-profile.less"),
      },
      url: "/send-change-password-request/",
      templateUrl: require("@gtmhub/users/userProfile/views/send-change-password-request.jade"),
      controller: "SendChangePasswordRequestCtrl",
    };
  }

  public userLanguage(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        titleKey: "user_profile",
        trackingPage: "User Language",
        styleUrl: require("@gtmhub/users/userProfile/less/user-profile.less"),
      },
      url: "/user-language",
      templateUrl: require("@gtmhub/users/userProfile/views/user-language.jade"),
      controller: "UserLanguageCtrl",
    };
  }

  public editUserLicense(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        trackingPage: "Edit User License",
      },
      url: "edit-license/",
      params: {
        id: null,
      },
      views: {
        "editModal@": {
          templateUrl: require("@gtmhub/users/userProfile/views/edit-license.jade"),
          controller: "EditUserLicenseCtrl",
        },
      },
    };
  }

  public _portalSection(): Ng1StateDeclaration {
    return modal({
      view: "cancelSubscriptionView",
      windowClass: "modal-small cancel-subscription-modal",
      abstract: true,
    });
  }

  public cancelSubscription(): Ng1StateDeclaration {
    return {
      url: "cancel-subscription/",
      data: {
        trackingPage: "Cancel Subscription",
      },
      views: {
        "cancelSubscriptionView@": {
          templateUrl: require("./configuration/views/billing/cancel-subscription.jade"),
          controller: "CancelSubscriptionCtrl",
        },
      },
    };
  }
}

class WorkdayState {
  public workday(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        titleKey: "Workday",
      },
      url: "/workday/",
    };
  }

  public wizard(): Ng1StateDeclaration {
    return {
      data: {
        trackingPage: "Workday wizard",
        styleUrl: require("./configuration/workday/wizard/wizard.less"),
      },
      url: "wizard/",
      templateUrl: require("./configuration/workday/wizard/wizard.jade"),
      controller: "WizardCtrl",
      controllerAs: "$ctrl",
      resolve: {
        _featureAvailable: featureAvailable("setup.workday") && featureToggleEnabled("workday-provisioning"),
      },
    };
  }

  public wizardProvisioning(): Ng1StateDeclaration {
    return {
      url: "provisioning/",
      data: {
        trackingPage: "Wizard Provisioning",
      },
      views: {
        "wizard-provisioning": {
          component: "provisioning",
        },
      },
    };
  }

  public wizardSelections(): Ng1StateDeclaration {
    return {
      url: "selections/",
      data: {
        trackingPage: "Wizard Selections",
      },
      views: {
        "wizard-selections": {
          component: "wizardSelections",
        },
      },
    };
  }

  public _wizardSidebar(): Ng1StateDeclaration {
    return modal({
      view: "modalview",
      windowClass: "sidebar-modal wizard-sidebar-modal",
      backdropClass: "dark-backdrop",
      abstract: true,
      backdrop: false,
    });
  }

  public wizardSelection(): Ng1StateDeclaration {
    return {
      url: "advanced-criteria/?selectionId",
      views: {
        "modalview@": {
          templateUrl: require("./configuration/workday/wizard/selections/selection/selection.jade"),
          controller: "WizardSelectionCtrl",
          controllerAs: "$ctrl",
        },
      },
      data: {
        titleExpression: "$ctrl.selection.name",
        styleUrl: require("./configuration/workday/wizard/selections/selection/selection.less"),
      },
    };
  }
  public wizardMapping(): Ng1StateDeclaration {
    return {
      url: "mapping/",
      data: {
        trackingPage: "Wizard Mapping",
      },
      views: {
        "wizard-mapping": {
          component: "wizardMapping",
        },
      },
    };
  }

  public wizardMap(): Ng1StateDeclaration {
    return {
      url: "map/?teamId",
      data: {
        styleUrl: require("./configuration/workday/wizard/mapping/manual-organization-team-mapping-modal/manual-organization-team-mapping-modal.less"),
      },
      views: {
        "modalview@": {
          templateUrl: require("./configuration/workday/wizard/mapping/manual-organization-team-mapping-modal/manual-organization-team-mapping-modal.jade"),
          controller: "ManualOrganizationTeamMappingModalCtrl",
          controllerAs: "$ctrl",
        },
      },
    };
  }

  public workdaySettings(): Ng1StateDeclaration {
    return {
      url: "settings/",
      data: {
        trackingPage: "Workday Settings",
      },
      views: {
        settings: {
          templateUrl: require("./configuration/workday/workday-settings/workday-settings.jade"),
          controller: "WorkdaySettingsCtrl",
          controllerAs: "$ctrl",
        },
      },
    };
  }

  public syncHistory(): Ng1StateDeclaration {
    return {
      url: "sync-history/",
      data: {
        trackingPage: "Sync History",
      },
      views: {
        "sync-history": {
          templateUrl: require("./configuration/workday/sync-history/sync-history.jade"),
          controller: "SyncHistoryCtrl",
          controllerAs: "$ctrl",
        },
      },
    };
  }
}

class RoleStates {
  private modal = null;

  public roles(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        titleKey: "manage_roles",
        trackingPage: "Manage Roles",
      },
      url: "/roles/",
      templateUrl: require("./roles/views/roles.jade"),
      controller: "RolesCtrl",
    };
  }

  public _createRole(): Ng1StateDeclaration {
    return modal({
      view: "modalview",
      abstract: true,
      windowClass: "medium-small role-editor-modal",
    });
  }

  public createRoleForm(): Ng1StateDeclaration {
    return {
      url: "create/",
      data: {
        trackingPage: "Create Role",
      },
      views: {
        "modalview@": {
          component: "roleEditor",
        },
      },
    };
  }

  public editRole(): Ng1StateDeclaration {
    return {
      url: "edit/:id/",
      data: {
        trackingPage: "Edit Role",
      },
      params: {
        id: null,
      },
      views: {
        "modalview@": {
          component: "roleEditor",
        },
      },
    };
  }

  public permissions(): Ng1StateDeclaration {
    return {
      url: "permissions/:id/",
      data: {
        trackingPage: "Permissions Details",
      },
      params: {
        id: null,
      },
      views: {
        "modalview@": {
          templateUrl: require("./roles/views/permissions.jade"),
          controller: "PermissionsCtrl",
        },
      },
    };
  }

  // eslint-disable-next-line sonarjs/no-identical-functions
  public createRoleDone(): Ng1StateDeclaration {
    return {
      onEnter: [
        "$state",
        ($state: StateService) => {
          this.modal.close();
          $state.go("^.^");
        },
      ],
    };
  }
}

class GoalStates {
  private modal: IModalServiceInstance = null;

  public goals(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        titleExpression: "planningSession.title",
        trackingPage: "Session Page Opened",
      },
      params: {
        planningSessionId: null,
      },
      url: "/goals/:planningSessionId/",
      templateUrl: require("./goals/views/goals.jade"),
      controller: "GoalsCtrl",
    };
  }

  public nested(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        trackingPage: "OKRs Alignment Nested",
      },
      url: "nested/",
      views: {
        alignment: {
          templateUrl: require("./okrs/alignment-tree/states/alignment-nested.jade"),
          controller: "AlignmentNestedCtrl",
        },
      },
      reloadOnSearch: false,
    };
  }

  public alignmentTree(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        trackingPage: "OKRs Alignment Tree",
      },
      url: "tree/",
      views: {
        alignmentTree: {
          templateUrl: require("./okrs/alignment-tree/states/alignment-tree.jade"),
          controller: "AlignmentTreeCtrl",
        },
      },
      reloadOnSearch: false,
    };
  }

  public alignmentSidebar(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        trackingPage: "OKRs Alignment Sidebar",
      },
      params: {
        locateId: null,
      },
      url: "alignment/?sharedId&sharedParentId",
      templateUrl: require("./okrs/alignment-tree/states/alignment-sidebar.jade"),
      controller: "AlignmentSidebarCtrl",
      reloadOnSearch: false,
    };
  }

  public _addSlice(): Ng1StateDeclaration {
    return modal({
      view: "addslice",
      abstract: true,
      windowClass: "medium-small",
    });
  }

  public addSlice(): Ng1StateDeclaration {
    return {
      url: "add-slice/:metricId/",
      data: {
        metricId: null,
        modal: null,
        requiresLogin: true,
        trackingPage: "Add Metric Slice",
      },
      params: {},
      views: {
        "addslice@": {
          templateUrl: require("./okrs/alignment-tree/states/add-slice.jade"),
          controller: "AddSliceCtrl",
        },
      },
    };
  }

  public allGoals(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        trackingPage: "Session Grid View",
      },
      // user reload on search if the url has params like below, to avoid re-initialisation of the controller when transitioning states
      reloadOnSearch: false,
      url: "all/?metricOwnerIds&ownerIds&tags&teamAndMemberIds&sort&page&attainment&confidence&metricBefore&metricAfter&sessions",
      templateUrl: require("./goals/views/all.jade"),
      controller: "AllGoalsCtrl",
    };
  }

  public goalDetails(): Ng1StateDeclaration {
    return {
      data: {
        titleExpression: "goal.name",
        trackingPage: "Goal Details",
      },
      params: {
        openingState: null,
      },
      // when navigating to goal view in OKR grid(from single team view, for example) query params cause controller to reload if not set to false
      reloadOnSearch: false,
      url: ":id/metrics/?sharedGoal",
      views: {
        "goaldetails@": {
          templateUrl: require("./goals/views/goal/goal.jade"),
          controller: "GoalCtrl",
        },
      },
    };
  }

  public _marketplace(): Ng1StateDeclaration {
    return modal({
      view: "marketplacemodalview",
      abstract: true,
      windowClass: "medium",
    });
  }

  public submit(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        trackingPage: "Submit Goal To Marketplace",
      },
      params: {
        goalsIds: null,
      },
      url: "submit-to-marketplace/:goalToBeSubmittedId/",
      views: {
        "marketplacemodalview@": {
          templateUrl: require("./goals/views/submit-to-marketplace.jade"),
          controller: "SubmitToMarketplaceCtrl",
        },
      },
    };
  }

  public _singleGoal(): Ng1StateDeclaration {
    return modal({
      view: "goaldetails",
      windowClass: "goal-sidebar",
      backdropClass: "dark-backdrop",
      abstract: true,
    });
  }

  // eslint-disable-next-line sonarjs/no-identical-functions
  public _closeSingleGoal(): Ng1StateDeclaration {
    return {
      data: {},
      onEnter: () => {
        this.modal.close();
      },
    };
  }

  public _timeline(): Ng1StateDeclaration {
    return modal({
      view: "timeline",
      windowClass: "sidebar-modal",
      abstract: true,
    });
  }

  public timeline(): Ng1StateDeclaration {
    return {
      url: "timeline/",
      data: {
        trackingPage: "Goal Details Timeline",
      },
      views: {
        "timeline@": {
          templateUrl: require("./goals/views/goal/details/timeline.jade"),
          controller: "GoalTimelineCtrl",
        },
      },
    };
  }

  public _form(): Ng1StateDeclaration {
    return modal({
      view: "goalform",
      windowClass: "medium-large create-form create-goal",
      backdropClass: "dark-backdrop",
      abstract: true,
    });
  }

  public goalForm(): Ng1StateDeclaration {
    const details: Ng1StateDeclaration = {
      templateUrl: require("./goals/views/form/form.jade"),
      controller: "EditGoalCtrl",
    };

    return {
      url: "form/?parentId&externalSessionId&teamId&parentType",
      // when navigating to goal form in OKR grid(from session conflicts modal, for example) query params cause controller to reload if not set to false
      // goal form post-init behaviour does not depend on query/search params
      reloadOnSearch: false,
      params: {
        id: null,
        isModal: true,
        externalSessionId: null,
        teamId: null,
      },
      data: {
        trackingPage: "Create Objective Modal",
      },
      views: {
        "goalform@": details,
      },
    };
  }

  public bulk(): Ng1StateDeclaration {
    return modal({
      view: "modalview",
      backdrop: true,
      windowClass: "sidebar-modal medium-small goals-bulk-actions",
      abstract: true,
      url: "bulk/",
    });
  }

  public assign(): Ng1StateDeclaration {
    return {
      url: "assign/",
      onEnter: [
        "$rootScope",
        "$timeout",
        ($rootScope: IGtmhubRootScopeService, $timeout: ITimeoutService) => {
          $timeout(() => ($rootScope.bulkAction = "assign")); // trigger on next digest cycle
        },
      ],
      onExit: [
        "$rootScope",
        ($rootScope: IGtmhubRootScopeService) => {
          $rootScope.bulkAction = null;
        },
      ],
      views: {
        "modalview@": {
          templateUrl: require("./goals/views/bulk-assign.jade"),
          controller: "BulkAssignCtrl",
        },
      },
      params: {
        sessionSettings: null,
      },
    };
  }

  public tag(): Ng1StateDeclaration {
    return {
      onEnter: [
        "$rootScope",
        "$timeout",
        ($rootScope: IGtmhubRootScopeService, $timeout: ITimeoutService) => {
          $timeout(() => ($rootScope.bulkAction = "tag")); // trigger on next digest cycle
        },
      ],
      onExit: [
        "$rootScope",
        ($rootScope: IGtmhubRootScopeService) => {
          $rootScope.bulkAction = null;
        },
      ],
      url: "tag/",
      views: {
        "modalview@": {
          component: "bulkTag",
        },
      },
      params: {
        sessionId: null,
      },
    };
  }

  public _createMetric(): Ng1StateDeclaration {
    return modal({
      view: "createmetric",
      windowClass: "medium-large create-form",
      backdropClass: "dark-backdrop",
      abstract: true,
    });
  }

  public createMetricForm(): Ng1StateDeclaration {
    return {
      url: "create-metric/",
      data: {
        requiresLogin: true,
        trackingPage: "Create Manual Key Result",
      },
      params: {
        id: null,
        isNewMetricAdded: null,
      },
      views: {
        "createmetric@": {
          templateUrl: require("./okrs/metrics/views/create-manual-metric.jade"),
          controller: "MetricFormCtrl",
        },
      },
    };
  }

  public _updateMetric(): Ng1StateDeclaration {
    return modal({
      view: "updatemetric",
      windowClass: "medium modal-overflow-x-unset",
      backdropClass: "dark-backdrop",
      abstract: true,
    });
  }

  public updateMetricForm(): Ng1StateDeclaration {
    return {
      url: "update-metric/:metricId/",
      params: {
        editOn: false,
      },
      data: {
        trackingPage: "Update Key Result",
      },
      views: {
        "updatemetric@": {
          templateUrl: require("./okrs/metrics/views/update-manual-metric.jade"),
          controller: "UpdateMetricCtrl",
        },
      },
    };
  }

  public editMetricForm(): Ng1StateDeclaration {
    return {
      url: "edit-metric-update/:checkinId/",
      params: {
        editOn: true,
      },
      data: {
        trackingPage: "Edit Key Result Note",
      },
      views: {
        "updatemetric@": {
          templateUrl: require("./okrs/metrics/views/update-metric-note.jade"),
          controller: "UpdateMetricCtrl",
        },
      },
    };
  }

  public _metric(): Ng1StateDeclaration {
    return modal({
      view: "metric",
      windowClass: "sidebar-modal no-padding large",
      backdropClass: "dark-backdrop",
      abstract: true,
    });
  }

  public metric(): Ng1StateDeclaration {
    return {
      data: {
        titleExpression: "metric.name",
        trackingPage: "Key Result Details",
      },
      // when navigating to metric view in OKR grid(from insight usage, for example) query params cause controller to reload if not set to false
      reloadOnSearch: false,
      url: "metric/:metricId/?sharedMetric",
      views: {
        "metric@": {
          templateUrl: require("./okrs/metrics/views/metric.jade"),
          controller: "MetricCtrl",
        },
      },
    };
  }

  public _editMetric(): Ng1StateDeclaration {
    return modal({
      view: "editmetric",
      windowClass: "medium-large create-form",
      backdropClass: "dark-backdrop",
      abstract: true,
    });
  }

  public editMetric(returnState = "^.^"): Ng1StateDeclaration {
    return {
      url: "edit-metric/:metricId/",
      data: {
        metricId: null,
        modal: null,
        requiresLogin: true,
        trackingPage: "Edit Key Result",
      },
      params: {
        isNewMetricAdded: null,
        planningSessionId: null,
        id: null,
        returnState,
      },
      views: {
        "editmetric@": {
          templateUrl: require("./okrs/metrics/views/create-manual-metric.jade"),
          controller: "MetricFormCtrl",
        },
      },
    };
  }

  public createDynamicMetric(): Ng1StateDeclaration {
    return {
      url: "create-dynamic-metric/",
      data: {
        modal: null,
        requiresLogin: true,
        trackingPage: "Create Dynamic Key Result",
      },
      params: {
        metric: null,
      },
      views: {
        "createmetric@": {
          templateUrl: require("./okrs/metrics/views/edit-dynamic-metric.jade"),
          controller: "EditDynamicMetricCtrl",
        },
      },
    };
  }

  public editDynamicMetric(): Ng1StateDeclaration {
    return {
      url: "edit-dynamic-metric/:metricId/",
      data: {
        metricId: null,
        modal: null,
        requiresLogin: true,
        trackingPage: "Edit Dynamic Key Result",
      },
      params: {
        id: null,
        isNewMetricAdded: null,
      },
      views: {
        "editmetric@": {
          templateUrl: require("./okrs/metrics/views/edit-dynamic-metric.jade"),
          controller: "EditDynamicMetricCtrl",
        },
      },
    };
  }

  public _approve(): Ng1StateDeclaration {
    return modal({
      view: "approvemodal",
      abstract: true,
      windowClass: "medium",
    });
  }

  public _review(): Ng1StateDeclaration {
    return modal({
      view: "reviewmodal",
      abstract: true,
      windowClass: "medium-small",
    });
  }

  public sendForApproval(): Ng1StateDeclaration {
    return {
      url: "send-for-approval/",
      data: {
        trackingPage: "Send Objective for Approval",
      },
      views: {
        "approvemodal@": {
          templateUrl: require("./goals/views/send-for-approval.jade"),
          controller: "SendForApprovalCtrl",
        },
      },
    };
  }

  public review(): Ng1StateDeclaration {
    return {
      url: "review/:action/",
      data: {
        trackingPage: "Objective Review",
      },
      views: {
        "reviewmodal@": {
          templateUrl: require("./goals/views/review.jade"),
          controller: "ReviewCtrl",
        },
      },
    };
  }
}

class HomeStates {
  public home(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        titleKey: "home",
        trackingPage: "Home Page",
        styleUrl: require("./home/home.less"),
      },
      params: {},
      url: "/home/",
      templateUrl: require("./home/views/home.jade"),
      controller: "HomeCtrl",
      resolve: {
        isBootstrap: [
          "$state",
          ($state: StateService): boolean => !$state || !$state.current || !$state.current.name || $state.current.name.includes("accountBootstrap"),
        ],
      },
    };
  }

  public msTeamsHome(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        titleKey: "home",
        trackingPage: "Home Page",
        styleUrl: require("./home/home.less"),
      },
      params: {},
      url: "/msteams/home/",
      templateUrl: require("./home/views/home.jade"),
      controller: "HomeCtrl",
      resolve: {
        isBootstrap: [
          "$state",
          ($state: StateService): boolean => !$state || !$state.current || !$state.current.name || $state.current.name.includes("accountBootstrap"),
        ],
      },
    };
  }

  public _comment(): Ng1StateDeclaration {
    return modal({
      view: "commentsview",
      windowClass: "sidebar-modal feed-comment-sidebar-modal",
      abstract: true,
    });
  }

  public thread(): Ng1StateDeclaration {
    return {
      url: ":targetId/comments",
      data: {
        trackingPage: "Comments",
      },
      views: {
        "commentsview@": {
          templateUrl: require("./feed/views/feed-comments-thread.jade"),
          controller: "FeedCommentsThreadCtrl",
        },
      },
    };
  }
}

class GlobalSearchStates {
  public globalSearch(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        trackingPage: "Global Search",
      },
      params: {
        facet: null,
        searchTerm: { dynamic: true },
        page: null,
      },
      url: "/search?facet&searchTerm&page",
      templateUrl: require("./search/components/global-search-state-wrapper/global-search-state-wrapper.jade"),
      controller: "GlobalSearchStateWrapperCtrl",
      controllerAs: "$ctrl",
    };
  }
}

class MultiAccountStates {
  public respondToMultiInvitation(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        trackingPage: "Multi Account Invitation Response Page",
      },
      url: "/multi-account-invitation/?invitationId&action",
      templateUrl: require("./multi-account/views/multi-account-invitation.jade"),
      controller: "MultiAccountInvitationResponseCtrl",
    };
  }
}
class PlanningSessionStates {
  private modal = null;

  public _sessionSelector(): Ng1StateDeclaration {
    return modal({
      view: "sessionselector",
      abstract: true,
      windowClass: "sidebar-modal session-selector",
      url: "session-selector/",
      data: {
        trackingPage: "Session Selector",
      },
    });
  }

  public _overview(): Ng1StateDeclaration {
    return modal({
      windowClass: "session-overview-modal",
      view: "sessionoverview",
      abstract: true,
      url: ":planningSessionId/",
      data: {
        trackingPage: "Session Overview",
      },
    });
  }

  public sessionSelector(): Ng1StateDeclaration {
    return {
      data: {
        trackingPage: "Sessions Selector",
      },
      params: {},
      url: "sessions/",
      views: {
        "sessionselector@": {
          templateUrl: require("./sessions/views/selector.jade"),
          controller: "SessionSelectorCtrl",
        },
      },
    };
  }

  public list(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        titleKey: "all_sessions",
        trackingPage: "All Sessions",
      },
      params: {},
      url: "/sessions/",
      templateUrl: require("./sessions/views/list.jade"),
      controller: "PlanningSessionsCtrl",
    };
  }

  // session overview  and stats state
  public overview(isModal: boolean): Ng1StateDeclaration {
    const overviewModal: Ng1StateDeclaration = {
      templateUrl: require("./sessions/views/overview-modal.jade"),
      controller: "PlanningSessionOverviewCtrl",
    };

    const overview: Ng1StateDeclaration = {
      templateUrl: require("./sessions/views/overview.jade"),
      controller: "PlanningSessionOverviewCtrl",
    };

    const state: Ng1StateDeclaration = {
      url: "overview/?tags",
    };

    if (isModal) {
      state.views = {
        "sessionoverview@": overviewModal,
      };
    } else {
      state.templateUrl = overview.templateUrl;
      state.controller = overview.controller;
    }

    return state;
  }

  public workflow(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        trackingPage: "Session Approval Progress",
      },
      params: {},
      url: "workflow/",
      templateUrl: require("./sessions/views/workflow.jade"),
      controller: "InspectWorkflowCtrl",
    };
  }

  public _inspect(): Ng1StateDeclaration {
    return modal({
      view: "inspect",
      abstract: true,
      data: {
        trackingPage: "Session Statistics Modal",
      },
      url: "inspect/",
      windowClass: "medium",
    });
  }

  public inspectList(): Ng1StateDeclaration {
    return {
      url: ":statName/",
      params: {
        modal: null,
        requiresLogin: true,
      },
      data: {
        trackingPage: "Session Statistics Tab",
      },
      views: {
        "inspect@": {
          templateUrl: require("./sessions/views/inspect.jade"),
          controller: "InspectStatisticsCtrl",
        },
      },
    };
  }

  public inspectGoals(): Ng1StateDeclaration {
    return {
      url: "goals/:goalsStatName/",
      params: {
        requiresLogin: true,
        page: null,
      },
      data: {
        trackingPage: "Session Workflow Inspect Goals",
      },
      views: {
        "inspect@": {
          templateUrl: require("./sessions/views/goals-workflow.jade"),
          controller: "InspectGoalsWorkflowCtrl",
        },
      },
    };
  }

  public inspectAssignees(): Ng1StateDeclaration {
    return {
      url: "assignees/:assigneesStatName/",
      params: {
        page: null,
        requiresLogin: true,
      },
      data: {
        trackingPage: "Session Workflow Inspect Assignees",
      },
      views: {
        "inspect@": {
          templateUrl: require("./sessions/views/assignees-workflow.jade"),
          controller: "InspectAssigneessWorkflowCtrl",
        },
      },
    };
  }

  public nudge(): Ng1StateDeclaration {
    return {
      url: "nudge/:targetName/",
      params: {
        requiresLogin: true,
      },
      data: {
        trackingPage: "Nudge Users Modal",
      },
      views: {
        "inspect@": {
          templateUrl: require("./sessions/views/nudge-people.jade"),
          controller: "NudgePeopleCtrl",
        },
      },
    };
  }

  // state to define or edit a session
  public sessionModal(): Ng1StateDeclaration {
    return modal({
      view: "sessionform",
      abstract: true,
      windowClass: "medium session-form",
      backdropClass: "dark-backdrop",
    });
  }

  public sessionForm(): Ng1StateDeclaration {
    return {
      url: "form/:id?/",
      params: { isCloningOperation: false },
      data: {
        trackingPage: "Session Form",
      },
      views: {
        "sessionform@": {
          templateUrl: require("./sessions/components/session-form/session-form.jade"),
          controller: "SessionFormCtrl",
          controllerAs: "$ctrl",
        },
      },
    };
  }

  public finish(): Ng1StateDeclaration {
    return {
      params: {
        goToGoals: null,
        planningSessionId: null,
      },
      url: "close/:planningSessionId/:goToGoals/",
      onEnter: [
        "$stateParams",
        ($stateParams: StateParams) => {
          this.modal.close({
            sessionId: $stateParams.planningSessionId,
            goToGoals: $stateParams.goToGoals,
          });
        },
      ],
    };
  }
  public _clone(): Ng1StateDeclaration {
    return modal({
      view: "sessionclone",
      data: {
        trackingPage: "Session Clone",
      },
      abstract: true,
      windowClass: "session-clone-modal medium",
      url: "clone/",
    });
  }

  public clone(): Ng1StateDeclaration {
    return {
      data: {
        trackingPage: "Copy From Session by Id",
      },
      params: {
        originSessionId: null,
      },
      url: "clone-session/?originSessionId",
      views: {
        "sessionclone@": {
          templateUrl: require("./sessions/views/clone-session.jade"),
          controller: "CloneSessionsCtrl",
        },
      },
    };
  }
}

class MiscStates {
  public dispatch(): Ng1StateDeclaration {
    return {
      url: "/dispatch?itemType&itemID&target&targetID",
      data: {
        requiresLogin: false,
      },
      template: "",
      controller: "DispatchCtrl",
    };
  }

  public _daterange(): Ng1StateDeclaration {
    return modal({
      view: "daterangeview",
      abstract: true,
      windowClass: "badge-templates modal-small",
    });
  }

  public daterange(): Ng1StateDeclaration {
    return {
      url: "select-date/",
      data: {
        trackingPage: "Select Date Modal",
      },
      views: {
        "daterangeview@": {
          templateUrl: require("./shared/views/daterange-picker.jade"),
          controller: "DateRangePickerCtrl",
        },
      },
    };
  }
}

class ConfigurationStates {
  private modal: IModalServiceInstance;

  public configuration(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        titleKey: "settings",
        trackingPage: "Configuration",
        styleUrl: require("./configuration/less/configuration.less"),
      },
      url: "/configuration/",
      templateUrl: require("./configuration/views/configuration.jade"),
      controller: "ConfigurationCtrl",
    };
  }

  public branding(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        trackingPage: "Settings Branding",
      },
      url: "branding/",
      templateUrl: require("./configuration/views/branding.jade"),
      controller: "BrandingCtrl",
    };
  }

  public accountNameOrDomainChange(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        trackingPage: "Settings Account Name Domain Change",
      },
      url: "account-name-domain-change/",
      templateUrl: require("./configuration/views/account-name-domain-change.jade"),
      controller: "AccountNameOrDomainChangeCtrl",
    };
  }

  public billing(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        trackingPage: "Settings Billing",
      },
      url: "billing/",
      templateUrl: require("./configuration/views/billing/billing.jade"),
      controller: "BillingCtrl",
    };
  }

  public configurationWorkday(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        trackingPage: "Settings Workday",
      },
      url: "workday/",
      templateUrl: require("./configuration/workday/workday.jade"),
      controller: "WorkdayConfigurationCtrl",
      controllerAs: "$ctrl",
      resolve: {
        _featureAvailable: featureAvailable("setup.workday") && featureToggleEnabled("workday-provisioning"),
      },
    };
  }

  public editionPlanChange(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        trackingPage: "Settings Edition Plan Change",
        titleKey: "choose_your_plan",
        styleUrl: require("./edition-plan-change/less/edition-plan-change.less"),
      },
      url: "/edition-plan-change/",
      templateUrl: require("./edition-plan-change/views/edition-plan-change.jade"),
      controller: "EditionPlanChangeCtrl",
      resolve: {
        resolveParams: (): IEditionPlanChangeResolveParams => ({
          isAccountActive: true,
          currentlySelectedPlanId: "",
        }),
      },
    };
  }

  public _cadence(): Ng1StateDeclaration {
    return modal({
      view: "cadence",
      abstract: true,
      data: {
        trackingPage: "Cadence Settings",
      },
      windowClass: "medium-small",
      url: "cadence-settings/",
    });
  }

  public cadence(): Ng1StateDeclaration {
    return {
      url: "cadence/",
      data: {
        modal: null,
        requiresLogin: true,
        styleUrl: require("./configuration/less/cadence-settings.less"),
        trackingPage: "Cadence",
      },
      views: {
        "cadence@": {
          component: "cadence",
        },
      },
    };
  }

  public coloring(): Ng1StateDeclaration {
    return {
      url: "coloring/",
      data: {
        modal: null,
        requiresLogin: true,
        trackingPage: "Settings Setup Color Scale Modal",
      },
      views: {
        "cadence@": {
          templateUrl: require("./configuration/views/coloring.jade"),
          controller: "ColoringCtrl",
        },
      },
    };
  }

  public confidence(): Ng1StateDeclaration {
    return {
      url: "confidence/",
      params: {
        returnState: "^.^",
      },
      data: {
        modal: null,
        requiresLogin: true,
        trackingPage: "Settings Setup Confidence Labels",
      },
      views: {
        "cadence@": {
          templateUrl: require("./configuration/views/confidence-settings.jade"),
          controller: "ConfidenceSettingsCtrl",
        },
      },
    };
  }

  public methodologyConfiguration(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        trackingPage: "Settings Methodology",
      },
      url: "methodology/",
      templateUrl: require("./configuration/views/methodology.jade"),
      controller: "ConfigurationCtrl",
    };
  }

  public languageConfiguration(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
      },
      url: "language/",
      component: "languageConfiguration",
    };
  }

  public reportsAndNotificationsConfiguration(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
      },
      url: "reports-and-notifications/",
      component: "reportsAndNotifications",
    };
  }

  public featuresConfiguration(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
      },
      url: "features/",
      templateUrl: require("./configuration/views/features.jade"),
      controller: "ConfigurationCtrl",
      resolve: {
        _featureAvailable: featureAvailable("modularity"),
      },
    };
  }

  public singleSignOnConfiguration(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        trackingPage: "Settings Single Sign On Configuration",
      },
      url: "single-sign-on/",
      templateUrl: require("./configuration/views/single-sign-on.jade"),
      controller: "SingleSignOnCtrl",
      resolve: {
        _featureAvailable: featureToggleEnabled(FeatureFlag.FeatureSSOSettings),
      },
    };
  }

  public slackConfiguration(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        trackingPage: "Settings Slack Configuration",
      },
      url: "slack/?error",
      templateUrl: require("./configuration/views/slack.jade"),
      controller: "SlackCtrl",
    };
  }

  public jiraConfiguration(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        trackingPage: "Settings Jira Configuration",
      },
      url: "jira/",
      templateUrl: require("./configuration/views/jira.jade"),
      controller: "JiraCtrl",
    };
  }

  public testConnection(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        trackingPage: "Settings Test Configuration",
      },
      url: "test/?confirmationUrl",
      abstract: false,
      views: {
        "modalview@": {
          templateUrl: require("./configuration/views/single-sign-on-connection.jade"),
          controller: "TestConnectionCtrl",
        },
      },
    };
  }

  public mappings(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        trackingPage: "Settings Mapping Configuration",
      },
      url: "mapping/",
      abstract: false,
      views: {
        "modalview@": {
          templateUrl: require("./configuration/views/single-sign-on-mappings.jade"),
          controller: "RoleMappingCtrl",
        },
      },
    };
  }

  public _connection(): Ng1StateDeclaration {
    return modal({
      view: "modalview",
      abstract: true,
      windowClass: "medium",
      url: "connection/",
    });
  }

  // eslint-disable-next-line sonarjs/no-identical-functions
  public connectionDone(): Ng1StateDeclaration {
    return {
      onEnter: [
        "$state",
        ($state: StateService) => {
          this.modal.close();
          $state.go("^.^");
        },
      ],
    };
  }

  public parameters(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        trackingPage: "Settings Insightboard Parameters",
      },
      url: "parameters/",
      templateUrl: require("./parameters/views/parameters.jade"),
      controller: "ParametersCtrl",
    };
  }

  public _parameterForm(): Ng1StateDeclaration {
    return modal({
      view: "modalview",
      data: {
        trackingPage: "Parameter Form",
      },
      abstract: true,
      windowClass: "medium-small",
      backdropClass: "dark-backdrop",
      url: "parameter/",
    });
  }

  public createParameter(): Ng1StateDeclaration {
    return {
      url: "create/",
      params: {
        modal: null,
        requiresLogin: true,
      },
      data: {
        trackingPage: "Settings Insightboard Parameters Edit Parameter",
      },
      views: {
        "modalview@": {
          templateUrl: require("./parameters/views/create-form.jade"),
          controller: "DefineParameterCtrl",
        },
      },
    };
  }

  public editParameter(): Ng1StateDeclaration {
    return {
      url: ":id/",
      params: {
        id: null,
        modal: null,
        requiresLogin: true,
      },
      data: {
        trackingPage: "Settings Insightboard Parameters Define New Parameter",
      },
      views: {
        "modalview@": {
          templateUrl: require("./parameters/views/create-form.jade"),
          controller: "DefineParameterCtrl",
        },
      },
    };
  }

  // eslint-disable-next-line sonarjs/no-identical-functions
  public parameterFormDone(): Ng1StateDeclaration {
    return {
      onEnter: [
        "$state",
        ($state: StateService) => {
          this.modal.close();
          $state.go("^.^");
        },
      ],
    };
  }

  public customFields(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        trackingPage: "Settings Custom Fields",
      },
      url: "custom-fields/?&page",
      templateUrl: require("./customFields/views/custom-fields.jade"),
      controller: "CustomFieldsCtrl",
    };
  }

  public _customField(): Ng1StateDeclaration {
    return modal({
      view: "modalview",
      abstract: true,
      data: {
        trackingPage: "Settings View Custom Field",
      },
      windowClass: "medium-small cf-modal",
      backdropClass: "dark-backdrop",
      url: "field/",
    });
  }

  public createCustomField(): Ng1StateDeclaration {
    return {
      url: "form/?id",
      params: {
        modal: null,
        requiresLogin: true,
      },
      data: {
        trackingPage: "Settings Create Custom Field",
      },
      views: {
        "modalview@": {
          templateUrl: require("./customFields/views/form.jade"),
          controller: "CustomFieldCtrl",
        },
      },
    };
  }

  public _issueToken(): Ng1StateDeclaration {
    return modal({
      view: "issuetoken",
      abstract: true,
      windowClass: "medium-small",
    });
  }

  public issueToken(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        trackingPage: "Settings API Tokens Issue Token",
      },
      params: {
        tokenType: null,
      },
      url: "issue-token/",
      views: {
        "issuetoken@": {
          templateUrl: require("./apiTokens/views/issue-token.jade"),
          controller: "IssueTokenCtrl",
        },
      },
    };
  }

  public apiTokens(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        trackingPage: "Settings API Tokens",
      },
      url: "api-tokens/",
      templateUrl: require("./apiTokens/views/index.jade"),
      controller: "ApiTokensController",
    };
  }

  public configurationScim(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        trackingPage: "Settings SCIM",
      },
      url: "scim/",
      component: "scimConfiguration",
      resolve: {
        _featureAvailable: featureAvailable("setup.scim") && featureToggleEnabled(FeatureFlag.ModuleScimAccountSettings),
      },
    };
  }

  public configurationTags(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        trackingPage: "Settings Tags",
      },
      url: "tags/?&page",
      component: "tagsGrid",
      resolve: {
        _featureAvailable: featureAvailable("setup.tags") && featureToggleEnabled(FeatureFlag.EnableTagManagement),
      },
    };
  }

  public _createTag(): Ng1StateDeclaration {
    return modal({
      view: "createtag",
      abstract: true,
      windowClass: "medium-small tag-editor-modal",
    });
  }

  public createTagForm(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        trackingPage: "Settings Tags Create Tag",
      },
      url: "create/",
      views: {
        "createtag@": {
          component: "tagForm",
        },
      },
    };
  }

  public _mergeTags(): Ng1StateDeclaration {
    return modal({
      view: "mergetags",
      abstract: true,
      windowClass: "medium-small merge-tags-modal",
    });
  }

  public createMergeTagsForm(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        trackingPage: "Settings Merge Tags",
      },
      url: "merge/",
      views: {
        "mergetags@": {
          component: "mergeTags",
        },
      },
      resolve: {
        _featureAvailable: featureAvailable("setup.tags") && featureToggleEnabled(FeatureFlag.EnableTagManagement),
      },
    };
  }
}

class ManageCustomFieldsOrderStates {
  public manageOrder(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        titleKey: "Custom fields order",
        styleUrl: require("./customFields/manage-order/manage-order.less"),
        trackingPage: "Settings Custom Fields Manage Order",
      },
      params: {},
      url: "/configuration/custom-fields/manage-order/",
      templateUrl: require("./customFields/manage-order/manage-order.jade"),
      controller: "ManageOrderCtrl",
      resolve: {
        _featureAvailable: featureToggleEnabled(FeatureFlag.FeatureReorderCustomFields),
      },
    };
  }
}
class ApproveBadgesStates {
  public approve(): Ng1StateDeclaration {
    return {
      data: {
        titleKey: "approve_badges",
        trackingPage: "Approve Badges",
      },
      url: "/badges/approve/",
      templateUrl: require("./badges/views/approve.jade"),
      controller: "ApproveBadgesCtrl",
      resolve: {
        _featureAvailable: featureToggleEnabled(FeatureFlag.ModuleBadges),
      },
    };
  }
}

class BadgeStates {
  public badges(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        trackingPage: "Manage Badges",
        titleKey: "manage_badges",
      },
      url: "/badges/",
      templateUrl: require("./badges/views/badges.jade"),
      controller: "BadgesCtrl",
      resolve: {
        _featureAvailable: featureToggleEnabled(FeatureFlag.ModuleBadges),
      },
    };
  }

  public _form(): Ng1StateDeclaration {
    return modal({
      view: "badgeform",
      data: {
        trackingPage: "Badge Form",
      },
      abstract: true,
      windowClass: "create-badge medium",
    });
  }

  public form(returnState: string, cancelState: string): Ng1StateDeclaration {
    const details: Ng1StateDeclaration = {
      templateUrl: require("./badges/views/form.jade"),
      controller: "BadgeFormCtrl",
    };

    const state: Ng1StateDeclaration = {
      url: ":badgeId/form/",
      data: {
        trackingPage: "Badge Form Details",
      },
      params: {
        id: null,
        returnState: returnState,
        cancelState: cancelState,
      },
    };

    state.views = {
      "badgeform@": details,
    };
    return state;
  }

  public _badgeAssignment(): Ng1StateDeclaration {
    return modal({
      view: "awardbadge",
      abstract: true,
      windowClass: "single-badge-preview medium",
    });
  }

  public badgeAssignment(): Ng1StateDeclaration {
    const details: Ng1StateDeclaration = {
      templateUrl: require("./badges/views/badge.jade"),
      controller: "BadgeCtrl",
    };

    return {
      url: "badge/:badgeAssignmentId/",
      data: {
        trackingPage: "Badge Assignment",
      },
      views: {
        "awardbadge@": details,
      },
    };
  }

  public _badgeTemplates(): Ng1StateDeclaration {
    return modal({
      view: "badgetemplatesview",
      abstract: true,
      windowClass: "badge-templates modal-small",
    });
  }

  public badgeTemplates(returnState: string, cancelState: string): Ng1StateDeclaration {
    const details: Ng1StateDeclaration = {
      templateUrl: require("./badges/views/templates.jade"),
      controller: "BadgeTemplatesCtrl",
    };

    const state: Ng1StateDeclaration = {
      url: "badge-templates/",
      data: {
        trackingPage: "Badge Templates",
      },
      params: {
        id: null,
        returnState: returnState,
        cancelState: cancelState,
      },
    };

    state.views = {
      "badgetemplatesview@": details,
    };
    return state;
  }
}

class EmployeeStates {
  public employees(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        titleKey: "employees",
        trackingPage: "Employees",
      },
      url: "/employees/",
      templateUrl: require("./employees/views/list.jade"),
      controller: "EmployeesCtrl",
    };
  }
}

class TeamStates {
  public _form(): Ng1StateDeclaration {
    return modal({
      view: "teamform",
      windowClass: "create-goal medium",
      backdropClass: "dark-backdrop",
      abstract: true,
      backdrop: true,
    });
  }

  public form(): Ng1StateDeclaration {
    return {
      url: "form/:teamId/?:avatar-selector-only&parentTeamId",
      params: {
        id: null,
      },
      data: {
        trackingPage: "Team Form",
      },
      views: {
        "teamform@": {
          templateUrl: require("./teams/views/form.jade"),
          controller: "TeamFormCtrl",
        },
      },
    };
  }

  public teams(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        titleKey: "teams_cap",
        trackingPage: "Teams Cap",
      },
      params: {},
      url: "/teams/",
      templateUrl: require("./teams/views/list.jade"),
      controller: "TeamsCtrl",
    };
  }

  public organization(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        titleKey: "organization",
        trackingPage: "Organization",
      },
      url: "/organization/",
      params: {},
      templateUrl: require("./teams/views/organization.jade"),
      controller: "OrganizationCtrl",
    };
  }

  public organizationChart(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        titleKey: "organization",
        trackingPage: "Organization",
      },
      url: "/organization-chart/",
      params: {},
      templateUrl: require("./teams/views/organization-chart.jade"),
      controller: "OrganizationChartCtrl",
      resolve: {
        _featureAvailable: featureAvailable("people.organization") && featureToggleEnabled(FeatureFlag.OrganizationChartV2),
      },
    };
  }

  public _manage(): Ng1StateDeclaration {
    return modal({
      view: "modalview",
      data: {
        trackingPage: "User Teams Manage",
      },
      windowClass: "user-teams-modal xl-large",
      abstract: true,
      backdrop: true,
    });
  }

  public manage(): Ng1StateDeclaration {
    return {
      url: "manage/",
      data: {
        trackingPage: "Manage Teams",
      },
      views: {
        "modalview@": {
          templateUrl: require("./teams/views/manage.jade"),
          controller: "ManageTeamsCtrl",
        },
      },
    };
  }
}

class ReportingStates {
  public reporting(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        trackingPage: "Reporting Contact Sales For Plan Upgrade",
      },
      url: "/reporting/",
      templateUrl: require("./reporting/views/contact-sales.jade"),
    };
  }
}

class AutomationStates {
  public automationContactSales(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        trackingPage: "Automation Contact Sales For Plan Upgrade",
      },
      url: "/automation/contact-sales/",
      templateUrl: require("./automation/views/contact-sales.jade"),
    };
  }

  public all(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        titleKey: "automation",
        trackingPage: "All Automations",
        styleUrl: require("./automation/less/automation.less"),
      },
      url: "/automation/all/",
      templateUrl: require("./automation/views/list.jade"),
      controller: "AutomationListCtrl",
    };
  }

  public automationSidebar(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        styleUrl: require("./automation/less/automation.less"),
        trackingPage: "Automation",
      },
      url: "/automation/",
      templateUrl: require("./automation/views/automation-sidebar.jade"),
      controller: "AutomationSidebarCtrl",
    };
  }

  public rules(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        titleExpression: "automation.name",
        trackingPage: "Automation Rules",
      },
      params: {
        automationId: null,
      },
      url: "rules/:automationId/",
      templateUrl: require("./automation/views/rules.jade"),
      controller: "AutomationRulesCtrl",
    };
  }

  public history(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        trackingPage: "Automation History",
      },
      params: {
        automationId: null,
      },
      url: "history/:automationId/",
      templateUrl: require("./automation/views/history.jade"),
      controller: "AutomationHistoryCtrl",
    };
  }

  public automationRules(): Ng1StateDeclaration {
    return modal({
      view: "rules",
      abstract: true,
      windowClass: "large",
    });
  }

  public _automation(): Ng1StateDeclaration {
    return modal({
      view: "automation",
      abstract: true,
      windowClass: "modal-small",
      data: {
        trackingPage: "Automation Modal",
      },
    });
  }

  public automationForm(): Ng1StateDeclaration {
    return {
      data: {
        modal: null,
        requiresLogin: true,
        trackingPage: "Automation Form",
      },
      views: {
        "automation@": {
          templateUrl: require("./automation/views/scratch.jade"),
          controller: "AutomationListCtrl",
        },
      },
    };
  }

  public triggerRule(): Ng1StateDeclaration {
    return {
      url: "trigger/",
      data: {
        modal: null,
        requiresLogin: true,
        trackingPage: "Automation Action Rule",
      },
      views: {
        "rules@": {
          templateUrl: require("./automation/views/trigger.jade"),
          controller: "AutomationTriggerCtrl",
        },
      },
    };
  }

  public conditionRule(): Ng1StateDeclaration {
    return {
      url: "condition/:automationTaskId/",
      data: {
        modal: null,
        requiresLogin: true,
        trackingPage: "Automation Trigger Rule",
      },
      views: {
        "rules@": {
          templateUrl: require("./automation/views/condition.jade"),
          controller: "AutomationConditionCtrl",
        },
      },
    };
  }

  public actionRule(): Ng1StateDeclaration {
    return {
      url: "action/:automationTaskId/:actionId/",
      data: {
        modal: null,
        requiresLogin: true,
        trackingPage: "Automation Condition Rule",
      },
      views: {
        "rules@": {
          templateUrl: require("./automation/views/action.jade"),
          controller: "AutomationActionCtrl",
        },
      },
    };
  }
}

class NotificationSettingsStates {
  public userNotificationSettings(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        titleKey: "notification_settings",
        trackingPage: "User Notification Settings",
      },
      // This is the correct URL in the emails. If you change it here you should change it in the emails too! :)
      url: "/hub/user-notification-settings/",
      templateUrl: require("./notifications/views/user-notification-settings.jade"),
      controller: "UserNotificationSettingsCtrl",
    };
  }

  public _accountNotificationSettings(): Ng1StateDeclaration {
    return modal({
      view: "accountNotificationSettingsView",
      abstract: true,
      windowClass: "large account-notifications-settings-modal",
    });
  }

  public accountNotificationSettings(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        trackingPage: "Account Notification Settings",
      },
      url: "account-notification-settings/",
      views: {
        "accountNotificationSettingsView@": {
          templateUrl: require("./notifications/views/account-notification-settings.jade"),
          controller: "AccountNotificationSettingsCtrl",
        },
      },
    };
  }
}

class InboxStates {
  public _message(): Ng1StateDeclaration {
    return modal({
      view: "messageview",
      windowClass: "sidebar-modal",
      abstract: true,
    });
  }

  public thread(): Ng1StateDeclaration {
    return {
      url: "thread/:targetId/:targetType/?insightName",
      data: {
        trackingPage: "Thread Details",
      },
      views: {
        "messageview@": {
          templateUrl: require("./comments/views/thread.jade"),
          controller: "ThreadCtrl",
        },
      },
    };
  }
}

class WorkflowStates {
  public workflow(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        titleKey: "okrs_for_approval",
        trackingPage: "Home Page OKRs For Approval",
      },
      url: "/workflow/",
      templateUrl: require("./workflow/views/workflow.jade"),
      controller: "WorkflowCtrl",
    };
  }
}

class ListStates {
  public lists(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        titleKey: "all_lists",
        trackingPage: "All Lists",
        styleUrl: require("./lists/less/module.less"),
      },
      params: {},
      url: "/lists/",
      templateUrl: require("./lists/views/lists.jade"),
      controller: "ListsCtrl",
    };
  }

  public list(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
        titleExpression: "list.title",
        trackingPage: "List",
        styleUrl: require("./lists/less/module.less"),
      },
      params: {
        isNewList: false,
      },
      url: "/lists/:listId/",
      templateUrl: require("./lists/views/list.jade"),
      controller: "ListCtrl",
    };
  }
}

// states below are used to redirect user when he is navigated to invalid/inexistant webapp url via notifications
// at some point if we have multiple redirects to one state we rename states to fromStartToDestination
class NotificationsUrlsRedirectStates {
  public toHomeMetric(): Ng1StateDeclaration {
    return {
      url: "/goals/metric/:metricId/",
      redirectTo: { state: "gtmhub.home._metric.metric" },
    };
  }

  public toHomeGoal(): Ng1StateDeclaration {
    return {
      url: "/goals/:id/metrics/",
      redirectTo: { state: "gtmhub.home._goalSidebar.goal" },
    };
  }

  public toTasksTask(): Ng1StateDeclaration {
    return {
      url: "/tasks/task/task-preview/:taskId/",
      redirectTo: { state: "gtmhub.tasks._task.task" },
    };
  }
}

// states to redirect from removed states (used in marketplace insights) to existing states
class LegacyRedirectStates {
  private dummyAbstractState = (): Ng1StateDeclaration => ({
    abstract: true,
  });

  public _singleGoal(): Ng1StateDeclaration {
    return this.dummyAbstractState();
  }

  public toInsightboardGoal(): Ng1StateDeclaration {
    return {
      url: "redirect-to-dashboard-goal-or-metric/:id/metrics/",
      redirectTo: { state: "gtmhub.insightboard._goalSidebar.goal" },
    };
  }

  public _metric(): Ng1StateDeclaration {
    return this.dummyAbstractState();
  }

  public toInsightboardMetric(): Ng1StateDeclaration {
    return {
      url: "redirect-to-dashboard-goal-or-metric/:id/metrics/metric/:metricId/",
      redirectTo: { state: "gtmhub.insightboard._goalSidebar.goal._metric.metric" },
    };
  }
}

// used for creating a dummy abstract state to handle all states for the google sites implementation:
class GoogleSitesStates {
  public googleSites(): Ng1StateDeclaration {
    return {
      data: {
        requiresLogin: true,
      },
      url: "/gsites",
    };
  }
}
